import { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Building2 } from "lucide-react";

import { text, searchAll } from "@/lib/memberDerive";
import { normalizePostList, postHref } from "@/lib/community";
import { activityWhen, activityPlace, searchPosts, excerpt } from "@/lib/v3Views";
import { useCore } from "../useCore";
import { requiredCollection } from "../requiredCollection";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, PrivacyNote } from "../components/kit";
import { V3Title, V3Actions, V3_PRIMARY } from "../components/v3";
import { SearchField, FilterBar, Chip, ResultTable, ResultRow, V3_GHOST } from "../components/v3app";

const TYPES = [["all", "Alles"], ["activities", "Aktivitäten"], ["groups", "Kreise"], ["members", "Menschen"], ["posts", "Beiträge"]];

function rowsFor(res, type) {
  const want = (k) => type === "all" || type === k;
  return [
    ...(want("activities") ? res.activities.map((a) => ({ key: `a-${a.id}`, initial: "A", title: text(a.title) || "Aktivität", context: `Aktivität · ${activityWhen(a)} · ${activityPlace(a)}`, to: `/aktivitaeten/${encodeURIComponent(a.id)}` })) : []),
    ...(want("groups") ? res.groups.map((g) => ({ key: `g-${g.id}`, initial: "K", title: text(g.name) || "Kreis", context: `Kreis${Number.isInteger(g.members_count) ? ` · ${g.members_count} Mitglieder` : ""}`, to: `/kreise/${encodeURIComponent(g.id)}` })) : []),
    ...(want("members") ? res.members.map((m) => ({ key: `m-${m.id}`, initial: "M", title: m.name || "Mitglied", context: `Mitglied${m.city ? ` · ${m.city}` : ""}`, to: `/menschen/${encodeURIComponent(m.id)}` })) : []),
    ...(want("posts") ? res.posts.map((p) => ({ key: `p-${p.id}`, initial: "B", title: excerpt(p.body, 60), context: `Beitrag · ${p.author.name}`, to: postHref(p.id) })) : []),
  ];
}

// M42: search over areas this account may see – activities, circles, members and visible posts; never private messages or Access Pass data.
export default function Search() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const results = useRef(null);
  const acts = useCore("/member/activities?category=all", (d) => requiredCollection(d, "activities"));
  const groups = useCore("/member/groups", (d) => requiredCollection(d, "groups"));
  const people = useCore("/member/directory", (d) => requiredCollection(d, "members"));
  const posts = useCore("/member/posts?audience=all", (d) => normalizePostList({ posts: requiredCollection(d, "posts") }));
  const res = useMemo(() => ({ ...searchAll({ activities: acts.data || [], groups: groups.data || [], members: people.data || [] }, q), posts: searchPosts(posts.data || [], q) }), [acts.data, groups.data, people.data, posts.data, q]);
  const postsUnavailable = posts.error && (type === "all" || type === "posts");
  const error = acts.error || groups.error || people.error || (type === "posts" && posts.error);
  const loading = !acts.data || !groups.data || !people.data || (!posts.data && !posts.error);
  const rows = rowsFor(res, type);
  const submit = (e) => { e.preventDefault(); results.current?.focus(); };
  return (
    <div data-m="M42" data-testid="search-page">
      <V3Title title="Suche über alle Bereiche" intro="Suche in den für dich sichtbaren öffentlichen und gemeinschaftlichen Bereichen." />
      <form onSubmit={submit} role="search">
        <SearchField id="search-q" label="Suchbegriff" placeholder="Suchbegriff, z. B. Kaffee" value={q} onChange={setQ} testid="search-input" />
        <FilterBar testid="search-filter" onReset={() => { setQ(""); setType("all"); }} canReset={!!q || type !== "all"}>
          <span className="sr-only">Bereich:</span>
          {TYPES.map(([k, l]) => <Chip key={k} pressed={type === k} onClick={() => setType(k)} testid={`search-type-${k}`}>{l}</Chip>)}
        </FilterBar>
        {error ? <ConnectionError onRetry={() => { acts.reload(); groups.reload(); people.reload(); posts.reload(); }} />
          : loading ? <Loading />
          : q.trim().length < 2 ? <p className="mt-[17px] text-[14px] text-v3-muted" data-testid="search-hint">Gib mindestens zwei Zeichen ein.</p>
          : (
            <div ref={results} tabIndex={-1} className="outline-none" role="region" aria-live="polite" aria-label={`${rows.length} Treffer`} data-testid="search-results">
              <ResultTable testid="search-table" label="Treffer">
                <ResultRow initial="S" title="Suchbegriff" context={`„${q.trim()}“ · ${rows.length} Treffer`} action="" testid="search-term" />
                {rows.map((r) => <ResultRow key={r.key} initial={r.initial} title={r.title} context={r.context} to={r.to} testid={`search-hit-${r.key}`} />)}
              </ResultTable>
              {postsUnavailable && <p className="mt-[11px] text-[14px] text-v3-muted" data-testid="search-posts-error">Beiträge konnten gerade nicht geladen werden; sie sind in diesen Treffern nicht enthalten.</p>}
              {!rows.length && !postsUnavailable && <p className="mt-[11px] text-[14px] text-v3-muted" data-testid="search-empty">Keine Treffer in den gewählten Bereichen.</p>}
            </div>
          )}
        <V3Actions className="mt-[21px]"><button type="submit" className={V3_PRIMARY} data-testid="search-submit">Suche anwenden</button></V3Actions>
      </form>
      <div className="mt-[16px]"><Link to="/organisationen" data-testid="search-organizations" className={V3_GHOST}><Building2 className="h-4 w-4" aria-hidden="true" />Organisationen entdecken</Link></div>
      <div className="mt-[16px]"><PrivacyNote>Private Nachrichten und dein Access Pass werden nie durchsucht.</PrivacyNote></div>
    </div>
  );
}
